import {
  ADMIN_PERMISSION_ID,
  PERMISSION_BY_ID,
  PERMISSION_ID,
  type AccessScope,
  type GrantPermission,
  type ProjectCapabilityGrant,
} from '@app/shared/interfaces/capability-grant.interface';
import { ADMIN_ROLE_ID } from '@app/shared/constants/general.constants';
import type { GlobalRole } from '@app/shared/interfaces/global-role.interface';
import type { ProjectUser } from '@app/shared/interfaces/project.interface';
import type { Routine } from '@app/shared/interfaces/routine.interface';
import type { Workflow } from '@app/shared/interfaces/workflow.interface';

/**
 * Pure mapping from the four server shapes this surface reads — members,
 * routines, workflows, grants — into the rows the Access tab renders.
 *
 * Kept free of Angular and HTTP so the rules that are easy to get subtly wrong
 * (an edit grant implying visibility, an admin being exempt from scoping) are
 * unit-testable directly. See `project-access.mapper.spec.ts`.
 */

/**
 * A stable identity for one control on the panel, used to track which single
 * control has a save in flight.
 *
 * Built from what the control acts on rather than its position, so it survives
 * the rows being rebuilt after a refetch.
 */
export function grantKey(
  userId: number,
  permission: GrantPermission,
  target: { resourceType: string; resourceId?: number; resourceName?: string }
): string {
  const resource =
    target.resourceId !== undefined ? `#${target.resourceId}` : `:${target.resourceName ?? ''}`;
  return `grant/${userId}/${permission}/${target.resourceType}${resource}`;
}

/** The identity of a member's scope switches, which write both axes together. */
export function scopeKey(userId: number): string {
  return `scopes/${userId}`;
}

/** The identity of a check-all / uncheck-all control on one capability column. */
export function bulkKey(userId: number, permission: GrantPermission): string {
  return `bulk/${userId}/${permission}`;
}

/** One capability on one resource, as a checkbox knows it. */
export interface GrantCell {
  /** Whether live authority exists — a revoked row reads as `false`. */
  granted: boolean;
  /** The live row's id, needed to revoke it. Absent when not granted. */
  grantId?: number;
  /**
   * Granted only by implication, not by a row of its own. Today this is
   * visibility implied by an edit grant: the server's `EditResource` satisfies a
   * `ViewResource` check, so the incoherent "may edit something invisible"
   * cannot be configured — and un-checking view here would do nothing while the
   * edit grant stands.
   */
  implied?: boolean;
}

/** A routine, with the two capabilities grantable on it. */
export interface RoutineAccess {
  routineId: number;
  name: string;
  run: GrantCell;
  approve: GrantCell;
}

/** A workflow, with the two capabilities grantable on it, keyed by permanent name. */
export interface WorkflowAccess {
  /** The permanent name — the grant key. Never a display name. */
  name: string;
  /**
   * Whether this workflow exists in the version being shown. False for a
   * workflow that is gone from this version but still carries a live grant,
   * which stays listed so the authority remains visible and revocable.
   */
  inVersion: boolean;
  view: GrantCell;
  edit: GrantCell;
}

/**
 * Which capabilities a member's **role** already confers on its own.
 *
 * Grants only ever add authority — nothing can withhold what a role baseline
 * already gives. So a grant of a capability the role already holds is inert: it
 * writes a row that confers nothing, and revoking it takes nothing away. A
 * checkbox for one is a switch that flips and does nothing, which is precisely
 * what the proposal refuses elsewhere (a restricted scope on an admin
 * membership).
 *
 * Derived from `GET /roles`, which returns each role's baseline permission ids,
 * rather than hardcoding role ids here: a service-only role added later gets the
 * right controls without a change to this file.
 */
export interface RoleBaseline {
  /** Whether the role already lets the member run every routine. */
  run: boolean;
  /** Whether the role already lets the member approve every review. */
  approve: boolean;
  /** Whether the role already lets the member publish versions. */
  publish: boolean;
  /**
   * Whether the role holds `Permission::Admin`, which exempts it from scoping.
   *
   * The server decides this by looking for the permission on the role, not by
   * comparing role ids — so an admin-capable role added later is handled here
   * without a change.
   */
  admin: boolean;
}

/** Builds a role-id → baseline lookup from `GET /roles`. */
export function toRoleBaselines(roles: GlobalRole[]): Map<number, RoleBaseline> {
  return new Map(
    roles.map((role) => {
      const held = new Set(role.permissions ?? []);
      return [
        role.roleId,
        {
          run: held.has(PERMISSION_ID.RunRoutine),
          approve: held.has(PERMISSION_ID.ApproveReview),
          publish: held.has(PERMISSION_ID.PublishVersion),
          admin: held.has(ADMIN_PERMISSION_ID),
        },
      ];
    })
  );
}

/**
 * The baseline assumed for a role the server did not describe.
 *
 * Fails toward *showing* the controls: a hidden control is invisible and
 * unexplained, while a shown one that turns out to be inert is at worst a grant
 * row that confers nothing. Neither is good, but only one is discoverable.
 */
const NO_BASELINE: RoleBaseline = {
  run: false,
  approve: false,
  publish: false,
  admin: false,
};

/** Everything the Access tab shows for one member. */
export interface MemberAccess {
  userId: number;
  userName: string;
  roleId: number;
  roleName: string;
  /** Admin memberships are exempt from scoping entirely. */
  isAdmin: boolean;
  /** The project owner, who can never be restricted. */
  isOwner: boolean;
  /**
   * Whether the scope switches apply at all. False for admins and the owner —
   * the server refuses to store a restriction for them, so offering the switch
   * would promise something it will not do.
   */
  restrictable: boolean;
  viewScope: AccessScope;
  editScope: AccessScope;
  /**
   * What this member's role already confers. Controls for a capability the role
   * holds are not offered — they could only write inert rows.
   */
  baseline: RoleBaseline;
  /** The project-scoped `PublishVersion` grant. */
  publish: GrantCell;
  routines: RoutineAccess[];
  workflows: WorkflowAccess[];
}

/** A grant row that currently confers authority. */
function isLive(grant: ProjectCapabilityGrant): boolean {
  return !grant.disabled;
}

/** The empty cell — no authority, nothing to revoke. */
const NONE: GrantCell = { granted: false };

/**
 * Finds the live grant of `permission` on a routine, if any.
 *
 * Revoked rows are skipped rather than shown greyed: the tab configures current
 * authority, and a revoked row is indistinguishable from one that never existed
 * as far as what the member can do. The audit trail is where withdrawals are
 * read.
 */
function routineCell(
  grants: ProjectCapabilityGrant[],
  routineId: number,
  permission: GrantPermission
): GrantCell {
  const match = grants.find(
    (g) =>
      isLive(g) &&
      g.resourceType === 'Routine' &&
      g.resourceId === routineId &&
      PERMISSION_BY_ID.get(g.permissionId) === permission
  );
  return match ? { granted: true, grantId: match.grantId } : NONE;
}

/** Finds the live grant of `permission` on a workflow, keyed by permanent name. */
function workflowCell(
  grants: ProjectCapabilityGrant[],
  name: string,
  permission: GrantPermission
): GrantCell {
  const match = grants.find(
    (g) =>
      isLive(g) &&
      g.resourceType === 'Workflow' &&
      g.resourceName === name &&
      PERMISSION_BY_ID.get(g.permissionId) === permission
  );
  return match ? { granted: true, grantId: match.grantId } : NONE;
}

/**
 * The workflow rows for one member: every workflow in the displayed version,
 * plus any the member still holds a live grant on.
 *
 * The trailing rows matter. Grants key on a permanent name and the server only
 * sweeps one when that name resolves in **no** version, so a workflow removed
 * from the current version but alive in an older published one keeps its grant —
 * and routines running that version still honour it. Listing only the current
 * catalog would hide that authority from the admin *and* leave no control to
 * revoke it, which is the opposite of what this panel is for.
 */
function toWorkflowAccess(
  grants: ProjectCapabilityGrant[],
  workflows: Workflow[]
): WorkflowAccess[] {
  const rowFor = (name: string, inVersion: boolean): WorkflowAccess => {
    const edit = workflowCell(grants, name, 'EditResource');
    const view = workflowCell(grants, name, 'ViewResource');
    return {
      name,
      inVersion,
      // An edit grant satisfies a visibility check on the server, so the view
      // cell reads as granted even with no view row of its own — otherwise the
      // tab would show a member as unable to see something they can edit.
      view: edit.granted && !view.granted ? { granted: true, implied: true } : view,
      edit,
    };
  };

  const rows = workflows.map((workflow) => rowFor(workflow.name, true));
  const known = new Set(workflows.map((workflow) => workflow.name));

  const orphaned = [
    ...new Set(
      grants
        .filter(
          (grant) =>
            isLive(grant) &&
            grant.resourceType === 'Workflow' &&
            grant.resourceName != null &&
            !known.has(grant.resourceName)
        )
        .map((grant) => grant.resourceName as string)
    ),
    // Workflow names are user-authored and can be non-ASCII; the default sort
    // orders by UTF-16 code unit, which puts accented names in the wrong place.
  ].sort((a, b) => a.localeCompare(b));

  return [...rows, ...orphaned.map((name) => rowFor(name, false))];
}

/** Finds the project-scoped `PublishVersion` grant. */
function publishCell(grants: ProjectCapabilityGrant[]): GrantCell {
  const match = grants.find(
    (g) =>
      isLive(g) &&
      g.resourceType === 'Project' &&
      PERMISSION_BY_ID.get(g.permissionId) === 'PublishVersion'
  );
  return match ? { granted: true, grantId: match.grantId } : NONE;
}

/**
 * Builds one member's row.
 *
 * `grants` must already be narrowed to this member — the caller groups by
 * `userId` once rather than re-scanning the project's rows per member per
 * resource.
 */
export function toMemberAccess(
  member: ProjectUser,
  grants: ProjectCapabilityGrant[],
  routines: Routine[],
  workflows: Workflow[],
  ownerUserId: number,
  baselines: Map<number, RoleBaseline>
): MemberAccess {
  const isOwner = member.userId === ownerUserId;
  const baseline = baselines.get(member.roleId) ?? NO_BASELINE;
  // Permission-derived first, so a future admin-capable role is handled without
  // a change here — but never *losing* the admin we already know about. A role
  // described with an empty permission list would otherwise read as
  // non-admin and be offered scope switches the server refuses, which is
  // exactly the inert control this panel exists not to render. The two rules
  // only ever add: neither can mark a non-admin as one.
  const isAdmin = baseline.admin || member.roleId === ADMIN_ROLE_ID;

  return {
    userId: member.userId,
    userName: member.userName,
    roleId: member.roleId,
    roleName: member.roleName,
    isAdmin,
    isOwner,
    restrictable: !isAdmin && !isOwner,
    // An admin's stored scopes are cleared on promotion, but read them defensively
    // rather than assuming: a stale row would otherwise render as a restriction
    // that is not enforced.
    viewScope: isAdmin ? 'All' : (member.viewScope ?? 'All'),
    editScope: isAdmin ? 'All' : (member.editScope ?? 'All'),
    baseline,
    publish: publishCell(grants),
    routines: routines.map((routine) => ({
      routineId: routine.routineId,
      name: routine.name,
      run: routineCell(grants, routine.routineId, 'RunRoutine'),
      approve: routineCell(grants, routine.routineId, 'ApproveReview'),
    })),
    workflows: toWorkflowAccess(grants, workflows),
  };
}

/** Builds every member's row, grouping the project's grants by member once. */
export function toMemberAccessList(
  members: ProjectUser[],
  grants: ProjectCapabilityGrant[],
  routines: Routine[],
  workflows: Workflow[],
  ownerUserId: number,
  baselines: Map<number, RoleBaseline>
): MemberAccess[] {
  const byUser = new Map<number, ProjectCapabilityGrant[]>();
  for (const grant of grants) {
    const existing = byUser.get(grant.userId);
    if (existing) {
      existing.push(grant);
    } else {
      byUser.set(grant.userId, [grant]);
    }
  }

  return members.map((member) =>
    toMemberAccess(
      member,
      byUser.get(member.userId) ?? [],
      routines,
      workflows,
      ownerUserId,
      baselines
    )
  );
}
